import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleExclamation,
  faLocationDot,
  faRotate,
  faRightToBracket,
  faMugSaucer,
  faPlay,
  faRightFromBracket,
} from "@fortawesome/free-solid-svg-icons";
import ControlHorariLayout from "./ControlHorariLayout";
import { chFetch, ChSessioCaducadaError } from "./chAuth";
import { chRegistrarEvent } from "./chFitxatge";
import { chLlistarPendents } from "./chIndexedDB";
import { chSincronitzarPendents } from "./chSync";
import { chDesarEstatCache, chLlegirEstatCache } from "./chCache";
import { chFusionarEventsAmbPendents, chEstatDesdeEvents } from "./chEstat";
import { CH_ETIQUETA_EVENT, CH_ICONA_EVENT, CH_CLASSE_EVENT } from "./chEtiquetes";
import { chParseDataHoraLocal, chFormatHora } from "./chDataHora";
import { useChEstatConnexio } from "./chConnexio";
import "./controlhorari.css";

const BOTONS_PER_TIPUS = {
  ENTRADA: { text: "Entrada", icona: faRightToBracket, classe: "ch-boto-blau" },
  INICI_PAUSA: { text: "Inici de pausa", icona: faMugSaucer, classe: "ch-boto-taronja" },
  FI_PAUSA: { text: "Fi de pausa", icona: faPlay, classe: "ch-boto-taronja" },
  SORTIDA: { text: "Sortida", icona: faRightFromBracket, classe: "ch-boto-fosc" },
};

function capitalitzar(text) {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function formatDataLlarga(data) {
  try {
    return capitalitzar(
      new Intl.DateTimeFormat("ca-ES", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(data)
    );
  } catch {
    return data.toLocaleDateString();
  }
}

function formatHoraViva(data) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(data.getHours())}:${pad(data.getMinutes())}:${pad(data.getSeconds())}`;
}

function formatDurada(segons) {
  const s = Math.max(0, Math.floor(segons));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
}

function formatDuradaHM(segons) {
  const s = Math.max(0, Math.floor(segons));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}`;
}

// Temps efectiu "en viu" (CURRENT_TASK_CALENDARI_I_COMPUTS.md §9): suma el
// temps mentre l'estat és TREBALLANT (des d'ENTRADA o FI_PAUSA fins al
// següent event, o fins "ara" si encara no n'hi ha), sense comptar cap
// pausa. És un indicador ràpid i senzill per a la pantalla d'inici: es
// congela sencer durant qualsevol pausa (tal com demana l'espec), a
// diferència del còmput rigorós de "Registres"/"Resum" (backend,
// ComputHelper.php), que sí que distingeix pauses que computen com a
// treball (ComputaTempsTreball) i és la xifra oficial per períodes.
function calcularTempsEfectiuMsEnViu(events, ara) {
  let hiHaEntrada = false;
  let ultimInici = null;
  let enPausa = false;
  let acumulatMs = 0;

  for (const e of events) {
    const t = chParseDataHoraLocal(e.DataHoraEvent);
    if (!t) continue;
    if (e.TipusEvent === "ENTRADA") {
      hiHaEntrada = true;
      ultimInici = t;
      enPausa = false;
    } else if (e.TipusEvent === "INICI_PAUSA") {
      if (ultimInici) acumulatMs += t - ultimInici;
      ultimInici = null;
      enPausa = true;
    } else if (e.TipusEvent === "FI_PAUSA") {
      ultimInici = t;
      enPausa = false;
    } else if (e.TipusEvent === "SORTIDA") {
      if (ultimInici) acumulatMs += t - ultimInici;
      ultimInici = null;
    }
  }

  if (!hiHaEntrada) return 0;
  if (ultimInici && !enPausa) {
    acumulatMs += ara - ultimInici;
  }
  return Math.max(0, acumulatMs);
}

// Suma la durada (en minuts) d'una llista de Trams "HH:MM:SS"/"HH:MM".
function sumarMinutsTrams(trams) {
  return (trams || []).reduce((acc, t) => {
    const [h1, m1] = (t.HoraInici || "0:0").split(":").map(Number);
    const [h2, m2] = (t.HoraFinal || "0:0").split(":").map(Number);
    return acc + (h2 * 60 + m2) - (h1 * 60 + m1);
  }, 0);
}

function salutacio(hora) {
  if (hora < 13) return "Bon dia";
  if (hora < 20) return "Bona tarda";
  return "Bona nit";
}

// Pantalla principal de /Control_Horari (CURRENT_TASK.md §15-§17 i
// CURRENT_TASK_SIMPLIFICAR_FLUX_FITXATGE.md): estat real, resum d'avui i
// botons de fitxatge segons l'estat. La pantalla intermèdia "Què vols
// registrar?" ja no forma part del flux; el selector de tipus de pausa
// continua a ControlHorariFitxar.
function ControlHorariInici() {
  const [estatServidor, setEstatServidor] = useState(null);
  const [pendents, setPendents] = useState([]);
  const [usantCache, setUsantCache] = useState(false);
  const [carregant, setCarregant] = useState(true);
  const [error, setError] = useState("");
  const [accioEnCurs, setAccioEnCurs] = useState(false);
  const [sincronitzant, setSincronitzant] = useState(false);
  const [ara, setAra] = useState(new Date());
  const { connectat: enLinia } = useChEstatConnexio();
  const navigate = useNavigate();

  const carregarPendents = useCallback(async () => {
    try {
      setPendents(await chLlistarPendents());
    } catch {
      // IndexedDB no disponible: es continua només amb el que digui el servidor.
    }
  }, []);

  const carregarEstat = useCallback(async () => {
    try {
      const resposta = await chFetch("/ControlHorariEstat.php");
      const dades = await resposta.json();
      if (!dades.success) {
        setError(dades.message || "No s'ha pogut carregar l'estat.");
        return;
      }
      setEstatServidor(dades);
      setUsantCache(false);
      setError("");
      chDesarEstatCache(dades);
    } catch (err) {
      if (err instanceof ChSessioCaducadaError) {
        navigate("/Control_Horari/login", { replace: true });
        return;
      }
      const cache = chLlegirEstatCache();
      if (cache?.estat) {
        setEstatServidor(cache.estat);
        setUsantCache(true);
        setError("");
      } else {
        setError("No s'ha pogut connectar amb el servidor i encara no hi ha cap dada guardada.");
      }
    } finally {
      setCarregant(false);
    }
  }, [navigate]);

  const sincronitzarISiCal = useCallback(
    async ({ tambeRecarregarEstat = true } = {}) => {
      setSincronitzant(true);
      try {
        const resultat = await chSincronitzarPendents();
        await carregarPendents();
        if (resultat.motiu === "sessio_caducada") {
          navigate("/Control_Horari/login", { replace: true });
          return;
        }
        if (resultat.sincronitzats > 0 && tambeRecarregarEstat && navigator.onLine) {
          await carregarEstat();
        }
      } finally {
        setSincronitzant(false);
      }
    },
    [carregarEstat, carregarPendents, navigate]
  );

  useEffect(() => {
    (async () => {
      await carregarPendents();
      await carregarEstat();
      await sincronitzarISiCal({ tambeRecarregarEstat: true });
    })();
    // Només en muntar la pantalla (CURRENT_TASK.md §13: "s'obre /Control_Horari").
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rellotge en viu (per l'hora actual i el comptador de pausa) i
  // recàrrega de l'estat quan l'app torna a primer pla (CURRENT_TASK.md §13).
  useEffect(() => {
    const interval = setInterval(() => setAra(new Date()), 1000);
    const alTornarVisible = () => {
      if (document.visibilityState === "visible") {
        carregarEstat();
        sincronitzarISiCal({ tambeRecarregarEstat: true });
      }
    };
    document.addEventListener("visibilitychange", alTornarVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", alTornarVisible);
    };
  }, [carregarEstat, sincronitzarISiCal]);

  useEffect(() => {
    const marcarOnline = () => sincronitzarISiCal({ tambeRecarregarEstat: true });
    window.addEventListener("online", marcarOnline);
    return () => window.removeEventListener("online", marcarOnline);
  }, [sincronitzarISiCal]);

  // El badge de pendents del capçalera avisa quan la cua canvia (p. ex. des
  // d'un altre component); ens hi enganxem també aquí per refrescar la llista.
  useEffect(() => {
    const alCanviar = () => carregarPendents();
    window.addEventListener("ch-pendents-canvi", alCanviar);
    return () => window.removeEventListener("ch-pendents-canvi", alCanviar);
  }, [carregarPendents]);

  const eventsFusionats = useMemo(
    () => chFusionarEventsAmbPendents(estatServidor?.events, pendents),
    [estatServidor, pendents]
  );
  const { ultimEvent, estatActual, transicionsPermeses } = useMemo(
    () => chEstatDesdeEvents(eventsFusionats),
    [eventsFusionats]
  );
  const horaEntradaEfectiva = useMemo(
    () => eventsFusionats.find((e) => e.TipusEvent === "ENTRADA")?.DataHoraEvent || estatServidor?.fullDiari?.HoraEntrada,
    [eventsFusionats, estatServidor]
  );
  const horaSortidaEfectiva = useMemo(
    () => [...eventsFusionats].reverse().find((e) => e.TipusEvent === "SORTIDA")?.DataHoraEvent,
    [eventsFusionats]
  );
  const tempsEfectiuMs = useMemo(
    () => calcularTempsEfectiuMsEnViu(eventsFusionats, ara),
    [eventsFusionats, ara]
  );

  // Previst d'avui (ja resolt pel backend com a TramsEfectius: Horari +
  // Calendari + Absències/Baixes/vigència ja aplicats) i diferència amb el
  // Temps efectiu en viu, per mostrar "Temps restant"/"Temps extra" al
  // costat del comptador.
  const previstMinutsAvui = useMemo(
    () => sumarMinutsTrams(estatServidor?.horariPrevist),
    [estatServidor]
  );
  const diferenciaMinuts = previstMinutsAvui > 0 ? tempsEfectiuMs / 60000 - previstMinutsAvui : null;

  const registrar = async (tipusEvent, idTipusPausa = null, nomTipusPausa = null) => {
    if (accioEnCurs) return;
    setAccioEnCurs(true);
    setError("");
    try {
      await chRegistrarEvent(tipusEvent, idTipusPausa, nomTipusPausa);
      await carregarPendents();
      if (navigator.onLine) await carregarEstat();
    } catch (err) {
      if (err instanceof ChSessioCaducadaError) {
        navigate("/Control_Horari/login", { replace: true });
        return;
      }
      setError(err.message || "No s'ha pogut registrar l'event.");
    } finally {
      setAccioEnCurs(false);
    }
  };

  const clicarBoto = (tipusEvent) => {
    if (tipusEvent === "INICI_PAUSA") {
      navigate("/Control_Horari/fitxar");
      return;
    }
    if (tipusEvent === "FI_PAUSA") {
      registrar(
        "FI_PAUSA",
        ultimEvent?.TipusPausaEmpleats_idTipusPausaEmpleats || null,
        ultimEvent?.TipusPausaNom || null
      );
      return;
    }
    registrar(tipusEvent);
  };

  if (carregant) {
    return (
      <ControlHorariLayout>
        <div className="ch-carregant">Carregant...</div>
      </ControlHorariLayout>
    );
  }

  const nom = estatServidor?.empleat?.Nom || "";

  return (
    <ControlHorariLayout ajuda="inici">
      <div className="ch-inici">
        <p className="ch-salutacio">
          {salutacio(ara.getHours())}{nom ? `, ${nom}` : ""}
        </p>
        <p className="ch-data-llarga">{formatDataLlarga(ara)}</p>
        <p className="ch-rellotge">{formatHoraViva(ara)}</p>

        {error && (
          <div className="ch-avis-error">
            <FontAwesomeIcon icon={faCircleExclamation} /> {error}
          </div>
        )}

        {pendents.length > 0 && (
          <div className="ch-avis-pendents">
            <span>
              {pendents.length} {pendents.length === 1 ? "registre pendent" : "registres pendents"} de sincronitzar
              {usantCache ? " · dades mostrades no actualitzades" : ""}
            </span>
            <button
              type="button"
              className="ch-boto-sincronitzar"
              onClick={() => sincronitzarISiCal({ tambeRecarregarEstat: true })}
              disabled={sincronitzant || !enLinia}
            >
              <FontAwesomeIcon icon={faRotate} spin={sincronitzant} /> {sincronitzant ? "Sincronitzant..." : "Sincronitzar ara"}
            </button>
          </div>
        )}

        {estatActual === "SENSE_JORNADA_INICIADA" && <p className="ch-subestat">Encara no has fitxat avui.</p>}
        {estatActual === "TREBALLANT" && horaEntradaEfectiva && (
          <p className="ch-subestat ch-subestat-blau">Treballant des de {chFormatHora(horaEntradaEfectiva)}</p>
        )}
        {estatActual === "EN_PAUSA" && ultimEvent && (
          <p className="ch-subestat ch-subestat-taronja">
            En pausa des de {chFormatHora(ultimEvent.DataHoraEvent)}
            {ultimEvent.TipusPausaNom ? ` · ${ultimEvent.TipusPausaNom}` : ""}
          </p>
        )}
        {estatActual === "JORNADA_FINALITZADA" && <p className="ch-subestat">Jornada finalitzada.</p>}
        <p className="ch-temps-efectiu">
          Temps efectiu: {formatDuradaHM(tempsEfectiuMs / 1000)}
          {diferenciaMinuts !== null && Math.round(diferenciaMinuts) !== 0 && (
            <span className={diferenciaMinuts > 0 ? "ch-temps-extra" : "ch-temps-restant"}>
              {" "}
              ({diferenciaMinuts > 0 ? "Temps extra" : "Temps restant"}: {formatDuradaHM(Math.abs(diferenciaMinuts) * 60)})
            </span>
          )}
        </p>

        {ultimEvent && (
          <div className="ch-targeta">
            <div className="ch-targeta-capcalera">
              <span>Últim registre</span>
            </div>
            <div className="ch-fila-event">
              <span className={`ch-icona-cercle ${CH_CLASSE_EVENT[ultimEvent.TipusEvent]}`}>
                <FontAwesomeIcon icon={CH_ICONA_EVENT[ultimEvent.TipusEvent]} />
              </span>
              <div className="ch-fila-event-text">
                <strong>
                  {CH_ETIQUETA_EVENT[ultimEvent.TipusEvent]}
                  {ultimEvent.TipusPausaNom ? ` · ${ultimEvent.TipusPausaNom}` : ""}
                  {ultimEvent._pendent && <span className="ch-etiqueta-pendent">Pendent</span>}
                </strong>
                <span>Avui {chFormatHora(ultimEvent.DataHoraEvent)}</span>
                {ultimEvent.EstatGPS === "OK" && (
                  <span className="ch-ubicacio">
                    <FontAwesomeIcon icon={faLocationDot} /> {Number(ultimEvent.Latitud).toFixed(4)},{" "}
                    {Number(ultimEvent.Longitud).toFixed(4)}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="ch-targeta">
          <div className="ch-targeta-capcalera">
            <span>Avui</span>
          </div>
          {eventsFusionats.length ? (
            eventsFusionats.map((ev) => (
              <div className={`ch-fila-event${ev._pendent ? " ch-pendent" : ""}`} key={ev.UUIDEvent}>
                <span className={`ch-icona-cercle ch-icona-petita ${CH_CLASSE_EVENT[ev.TipusEvent]}`}>
                  <FontAwesomeIcon icon={CH_ICONA_EVENT[ev.TipusEvent]} />
                </span>
                <div className="ch-fila-event-text">
                  <span>
                    {CH_ETIQUETA_EVENT[ev.TipusEvent]}
                    {ev.TipusPausaNom ? ` · ${ev.TipusPausaNom}` : ""}
                    {ev._pendent && !ev._errorPendent && <span className="ch-etiqueta-pendent">Pendent</span>}
                    {ev._errorPendent && <span className="ch-etiqueta-error">Error</span>}
                  </span>
                </div>
                <span className="ch-hora-dreta">{chFormatHora(ev.DataHoraEvent)}</span>
              </div>
            ))
          ) : (
            <p className="ch-text-suau">Encara no has fet cap registre avui.</p>
          )}
        </div>

        {estatServidor && (
          <div className="ch-targeta">
            <div className="ch-targeta-capcalera">
              <span>Horari previst avui</span>
            </div>
            {estatServidor.horariPrevist?.length > 0 ? (
              <>
                <p className="ch-text-suau">
                  {estatServidor.horariPrevist.map((t) => `${t.HoraInici.slice(0, 5)} – ${t.HoraFinal.slice(0, 5)}`).join(" · ")}
                </p>
                {estatServidor.absenciesParcials?.length > 0 && (
                  <p className="ch-text-suau ch-text-retallat">
                    Retallat per: {estatServidor.absenciesParcials
                      .map((a) => `${a.TipusNom} (${a.HoraInici?.slice(0, 5)}–${a.HoraFinal?.slice(0, 5)})`)
                      .join(", ")}
                  </p>
                )}
              </>
            ) : (
              <p className="ch-text-suau">
                Sense horari{estatServidor.motiuSenseHorari ? ` · ${estatServidor.motiuSenseHorari}` : ""}
              </p>
            )}
          </div>
        )}

        <div className="ch-accio-principal">
          {!enLinia && estatActual !== "JORNADA_FINALITZADA" && (
            <p className="ch-avis-offline">
              Sense connexió: pots continuar fitxant amb normalitat. Els registres es guardaran en aquest dispositiu i es
              sincronitzaran automàticament quan torni la connexió.
            </p>
          )}

          {transicionsPermeses.length === 0 ? (
            <p className="ch-text-suau">
              Entrada: {horaEntradaEfectiva ? chFormatHora(horaEntradaEfectiva) : "--"} · Sortida:{" "}
              {horaSortidaEfectiva ? chFormatHora(horaSortidaEfectiva) : "--"}
            </p>
          ) : (
            <div className="ch-llista-accions">
              {transicionsPermeses.map((tipusEvent) => {
                const cfg = BOTONS_PER_TIPUS[tipusEvent];
                const text =
                  tipusEvent === "FI_PAUSA" && ultimEvent
                    ? `${cfg.text} · ${formatDurada((ara - chParseDataHoraLocal(ultimEvent.DataHoraEvent)) / 1000)}`
                    : cfg.text;
                return (
                  <button
                    type="button"
                    key={tipusEvent}
                    className={`ch-boto-principal ${cfg.classe}`}
                    onClick={() => clicarBoto(tipusEvent)}
                    disabled={accioEnCurs}
                  >
                    <FontAwesomeIcon icon={cfg.icona} /> {accioEnCurs ? "Registrant..." : text}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </ControlHorariLayout>
  );
}

export default ControlHorariInici;
